import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import NextLink from "next/link";
import classNames from "classnames";

import { Typography } from "ui-kit/Typography";
import styles from "./styles.module.css";
import { MenuGroup } from "./types";
import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "ui-kit/icons";

const TopLevelButton = ({
  label,
  isExpanded,
  className,
}: {
  label: string;
  isExpanded?: boolean;
  className?: string;
}) => {
  return (
    <DropdownMenu.Trigger
      className={classNames(
        styles.topLevelItem,
        isExpanded && styles.isExpanded,
        className
      )}
    >
      <Typography
        variant="body3"
        style={{ textWrap: "nowrap" }}
        disableRedesignColor
      >
        <strong>{label}</strong>
      </Typography>
      <div className={styles.iconWrapper}>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
    </DropdownMenu.Trigger>
  );
};

export const TopLevelLink = ({
  href,
  label,
  className,
}: {
  href: string;
  label: string;
  className?: string;
}) => {
  return (
    <NextLink
      href={href}
      className={classNames(styles.topLevelItem, className)}
    >
      <Typography
        variant="body3"
        style={{ textWrap: "nowrap" }}
        disableRedesignColor
      >
        <strong>{label}</strong>
      </Typography>
    </NextLink>
  );
};

export const DesktopMenu = ({
  item,
  onMenuToggle,
  className,
}: {
  item: MenuGroup;
  onMenuToggle?: (state: boolean) => void;
  className?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    onMenuToggle && onMenuToggle(isExpanded);
  }, [isExpanded, onMenuToggle]);

  // Not modal: a site menu should leave the page scrollable and clickable,
  // as the Reach menu did.
  return (
    <DropdownMenu.Root
      modal={false}
      open={isExpanded}
      onOpenChange={setIsExpanded}
    >
      <TopLevelButton
        label={item.group}
        isExpanded={isExpanded}
        className={className}
      />

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={classNames(styles.menuList, styles.menuItems)}
          side="bottom"
          align="start"
        >
          {/* One child, so the list's gap stays between its own rows only. */}
          <div>
            {item.items.map((menuItem) => {
              return (
                <div key={menuItem.headline.name}>
                  {"subMenu" in menuItem ? (
                    <div key={menuItem.headline.name}>
                      <p className={styles.category}>
                        <strong>{menuItem.headline.name}</strong>
                      </p>
                      {menuItem.subMenu?.map((subMenuItem) => {
                        if (!subMenuItem.uri) {
                          return null;
                        }

                        return (
                          <DropdownMenu.Item asChild key={subMenuItem.name}>
                            <NextLink
                              className={styles.topLevelItem}
                              href={subMenuItem.uri}
                            >
                              <Typography variant="body3" disableRedesignColor>
                                <strong>{subMenuItem.name}</strong>
                              </Typography>
                            </NextLink>
                          </DropdownMenu.Item>
                        );
                      })}
                    </div>
                  ) : (
                    <DropdownMenu.Item asChild key={menuItem.headline.name}>
                      <NextLink
                        href={menuItem.headline.uri ?? ""}
                        className={styles.topLevelItem}
                      >
                        <Typography variant="body3" disableRedesignColor>
                          <strong>{menuItem.headline.name}</strong>
                        </Typography>
                      </NextLink>
                    </DropdownMenu.Item>
                  )}
                </div>
              );
            })}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
